import { Badge, Button, cn } from "@devhop/ui";
import {
  RiArrowDownSLine,
  RiArrowRightSLine,
  RiExternalLinkLine,
  RiStarFill,
  RiStarLine,
} from "@remixicon/react";
import { type ReactNode, useEffect, useState } from "react";
import type {
  FeedProductMonitorProduct,
  FeedProductMonitorTotals,
  FeedProductMonitorVariant,
} from "@/modules/cf-feed/presentation/api/types";
import { useBoundedInfiniteScroll } from "@/modules/cf-feed/presentation/lib/use-bounded-infinite-scroll";
import { AppImage } from "@/shared/ui/AppImage";
import { MonitorAnimatedValue } from "./MonitorAnimatedValue";
import { MonitorDisplayVariants } from "./MonitorDisplayVariants";
import { formatGroupedInteger, formatKip } from "./table-helpers";

const HIGHLIGHT_CARD_BG =
  "border-amber-500/70 bg-amber-100 dark:border-amber-500/50 dark:bg-amber-950/55";
const HIGHLIGHT_VARIANT_PANEL_BG =
  "border-amber-400/50 bg-amber-50/90 dark:bg-amber-950/35";
const HIGHLIGHT_VARIANT_ROW_BG =
  "border-amber-400/60 bg-amber-50 dark:border-amber-500/40 dark:bg-amber-950/45";
const DISPLAY_CARD_BORDER =
  "border-2 border-sky-500/70 ring-1 ring-sky-400/25 bg-card";

export type FeedProductMonitorTableProps = {
  products: FeedProductMonitorProduct[];
  totals?: FeedProductMonitorTotals | null;
  isLoading?: boolean;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
  /** pop-out display — สูงสุด 3 product */
  isProductOnDisplay?: (productId: string) => boolean;
  getProductDisplayPosition?: (productId: string) => number | null;
  onToggleProductDisplay?: (productId: string) => void;
  displayCount?: number;
  maxDisplayProducts?: number;
  displayToggleLabels?: { on: string; off: string };
  /** visual emphasis บนแท็บ monitor / pop-out (แยกจาก display) */
  isProductHighlighted?: (productId: string) => boolean;
  isVariantHighlighted?: (sessionProductId: string) => boolean;
  onToggleProductHighlight?: (productId: string) => void;
  onToggleVariantHighlight?: (sessionProductId: string) => void;
  emphasisToggleLabels?: { on: string; off: string };
  defaultExpandProducts?: boolean;
  emptyMessage?: string;
  /** หน้าจอ display — flash + delta เมื่อตัวเลข realtime เปลี่ยน */
  flashOnValueChange?: boolean;
  /** display หมุน 90/270 — คอลัมน์เดียว + ตัวอักษรเล็กลง กันทับ */
  compactDisplayLayout?: boolean;
  /** จอสินค้าเดียว: คิวก่อนหน้าอยู่ซ้าย คิวถัดไปอยู่ขวา */
  queueSides?: { previous: ReactNode; next: ReactNode };
  /** จอคนไลฟ์แสดง SKU ทั้งหมดเป็นการ์ดเตี้ย */
  compactVariants?: boolean;
};

function formatRemaining(variant: FeedProductMonitorVariant): string {
  if (variant.sessionRemaining == null) return "ບໍ່ຈຳກັດ";
  return String(variant.sessionRemaining);
}

/** สรุปคงเหลือระดับสินค้า — unlimited ที่มีของ → ບໍ່ຈຳກັດ; ไม่เช่นนั้นรวมตัวเลข (รวม 0 = หมด) */
function formatProductRemaining(product: FeedProductMonitorProduct): string {
  if (
    product.variants.some(
      (v) => v.allocationMode === "unlimited" && v.sessionRemaining == null,
    )
  ) {
    return "ບໍ່ຈຳກັດ";
  }
  const numeric = product.variants
    .map((v) => v.sessionRemaining)
    .filter((n): n is number => n != null);
  if (numeric.length === 0) return "ບໍ່ຈຳກັດ";
  return String(numeric.reduce((acc, n) => acc + n, 0));
}

function MonitorProductCard({
  product,
  open,
  onToggle,
  productOnDisplay = false,
  displayPosition = null,
  productHighlighted = false,
  isVariantHighlighted,
  onToggleProductDisplay,
  onToggleProductHighlight,
  onToggleVariantHighlight,
  displayToggleLabels,
  emphasisToggleLabels,
  flashOnValueChange = false,
  compactDisplayLayout = false,
  compactVariants = false,
}: {
  product: FeedProductMonitorProduct;
  open: boolean;
  onToggle: () => void;
  productOnDisplay?: boolean;
  displayPosition?: number | null;
  productHighlighted?: boolean;
  isVariantHighlighted?: (sessionProductId: string) => boolean;
  onToggleProductDisplay?: () => void;
  onToggleProductHighlight?: () => void;
  onToggleVariantHighlight?: (sessionProductId: string) => void;
  displayToggleLabels?: { on: string; off: string };
  emphasisToggleLabels?: { on: string; off: string };
  flashOnValueChange?: boolean;
  compactDisplayLayout?: boolean;
  compactVariants?: boolean;
}) {
  const displayOnLabel = displayToggleLabels?.on ?? "ຢູ່ໜ້າຈໍ";
  const displayOffLabel = displayToggleLabels?.off ?? "ເພີ່ມຂຶ້ນໜ້າຈໍ";
  const emphasisOnLabel = emphasisToggleLabels?.on ?? "ຍົກເລີກ";
  const emphasisOffLabel = emphasisToggleLabels?.off ?? "ເນັ້ນໃຫ້ເດັ່ນ";
  const hasActionButtons = Boolean(
    onToggleProductDisplay || onToggleProductHighlight,
  );

  if (compactVariants) {
    return (
      <article
        className={cn(
          "relative flex min-w-0 flex-col rounded-lg border border-border bg-card",
          productHighlighted && HIGHLIGHT_CARD_BG,
        )}
      >
        <header className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 p-3">
          <div className="flex min-w-0 flex-1 basis-40 items-center gap-3">
            <div className="size-12 shrink-0 overflow-hidden rounded border bg-muted">
              <AppImage
                src={product.imageUrl}
                alt={product.title}
                loading="eager"
                className="size-full object-cover"
                fallback={
                  <div className="flex size-full items-center justify-center text-muted-foreground text-xs">
                    —
                  </div>
                }
              />
            </div>
            <div className="min-w-0">
              <h2 className="break-words font-semibold text-lg leading-snug">
                {product.title}
              </h2>
              <p className="mt-0.5 text-muted-foreground text-sm">
                {product.variants.length} ຕົວເລືອກ
                {product.variants.length > 1 &&
                  ` · ຄົງເຫຼືອ ${formatProductRemaining(product)}`}
              </p>
            </div>
          </div>
          {productOnDisplay && (
            <Badge variant="secondary" className="shrink-0">
              ຄິວ {displayPosition ?? "-"} · {displayOnLabel}
            </Badge>
          )}
        </header>
        <MonitorDisplayVariants variants={product.variants} />
      </article>
    );
  }

  return (
    <article
      className={cn(
        "relative flex min-w-0 flex-col rounded-lg border transition-all duration-200",
        productHighlighted && HIGHLIGHT_CARD_BG,
        !productHighlighted && productOnDisplay && DISPLAY_CARD_BORDER,
        !productHighlighted && !productOnDisplay && "border-border bg-card",
      )}
    >
      {productHighlighted ? (
        <span
          className="pointer-events-none absolute inset-y-0 left-0 w-1.5 rounded-l-lg bg-amber-500"
          aria-hidden
        />
      ) : null}
      {productOnDisplay ? (
        <span className="pointer-events-none absolute top-2 left-2 z-10 rounded-md border border-sky-500/60 bg-sky-500/15 px-2 py-0.5 font-medium text-sky-900 text-xs dark:text-sky-100">
          ຄິວ {displayPosition ?? "-"} · {displayOnLabel}
        </span>
      ) : null}
      {hasActionButtons ? (
        <div className="absolute top-2 right-2 z-10 flex flex-col items-end gap-1">
          {onToggleProductDisplay ? (
            <Button
              type="button"
              variant={productOnDisplay ? "default" : "outline"}
              size="sm"
              className={cn(
                "h-7 gap-1 px-2 text-xs",
                productOnDisplay &&
                  "border-sky-600 bg-sky-600 shadow-sm hover:bg-sky-700",
              )}
              aria-pressed={productOnDisplay}
              onClick={(event) => {
                event.stopPropagation();
                onToggleProductDisplay();
              }}
            >
              <RiExternalLinkLine className="size-3.5" />
              {productOnDisplay ? displayOnLabel : displayOffLabel}
            </Button>
          ) : null}
          {onToggleProductHighlight ? (
            <Button
              type="button"
              variant={productHighlighted ? "default" : "outline"}
              size="sm"
              className={cn(
                "h-7 gap-1 px-2 text-xs",
                productHighlighted &&
                  "border-amber-600 bg-amber-600 shadow-sm hover:bg-amber-700",
              )}
              aria-pressed={productHighlighted}
              onClick={(event) => {
                event.stopPropagation();
                onToggleProductHighlight();
              }}
            >
              {productHighlighted ? (
                <RiStarFill className="size-3.5" />
              ) : (
                <RiStarLine className="size-3.5" />
              )}
              {productHighlighted ? emphasisOnLabel : emphasisOffLabel}
            </Button>
          ) : null}
        </div>
      ) : null}
      <Button
        type="button"
        variant="ghost"
        className={cn(
          "h-auto w-full min-w-0 justify-start gap-3 rounded-none rounded-t-lg p-4 text-left",
          compactVariants && "shrink-0 p-3 disabled:opacity-100",
          hasActionButtons && "pt-16",
          productHighlighted && "pl-5",
          productHighlighted &&
            "hover:bg-amber-200/50 dark:hover:bg-amber-900/40",
          flashOnValueChange &&
            compactDisplayLayout &&
            "flex-col items-stretch gap-3",
          flashOnValueChange &&
            compactDisplayLayout &&
            productOnDisplay &&
            "pt-10",
        )}
        aria-expanded={compactVariants ? undefined : open}
        disabled={compactVariants}
        onClick={onToggle}
      >
        <span
          className={cn(
            "flex min-w-0 items-center gap-3",
            flashOnValueChange && compactDisplayLayout && "w-full",
          )}
        >
          {compactVariants ? null : open ? (
            <RiArrowDownSLine className="size-4 shrink-0 text-muted-foreground" />
          ) : (
            <RiArrowRightSLine className="size-4 shrink-0 text-muted-foreground" />
          )}
          <div
            className={cn(
              "size-14 shrink-0 overflow-hidden rounded border bg-muted",
              productHighlighted &&
                "size-16 border-2 border-amber-500/60 ring-2 ring-amber-400/30",
              compactDisplayLayout && "size-12",
            )}
          >
            <AppImage
              src={product.imageUrl}
              alt={product.title}
              loading={compactDisplayLayout ? "eager" : "lazy"}
              className="size-full object-cover"
              fallback={
                <div className="flex size-full items-center justify-center text-muted-foreground text-xs">
                  —
                </div>
              }
            />
          </div>
          <span className="min-w-0 flex-1 overflow-hidden">
            <span
              className={cn(
                "block leading-snug",
                compactDisplayLayout
                  ? "whitespace-normal break-words"
                  : "truncate",
                flashOnValueChange
                  ? compactDisplayLayout
                    ? "font-semibold text-lg"
                    : "font-semibold text-xl"
                  : productHighlighted
                    ? "font-semibold"
                    : "font-medium",
                productHighlighted && "text-amber-950 dark:text-amber-100",
              )}
            >
              {product.title}
            </span>
            <span
              className={cn(
                "mt-0.5 block",
                flashOnValueChange && !compactDisplayLayout
                  ? "text-base"
                  : "text-sm",
                productHighlighted
                  ? "font-medium text-amber-900/80 dark:text-amber-200/80"
                  : "text-muted-foreground",
              )}
            >
              {product.variants.length} ຕົວເລືອກ
              {compactVariants && ` · ຄົງເຫຼືອ ${formatProductRemaining(product)}`}
            </span>
          </span>
          {!compactVariants &&
          (!compactDisplayLayout || !flashOnValueChange) ? (
            <span className="min-w-0 shrink-0 text-right">
              <span
                className={cn(
                  "block",
                  flashOnValueChange ? "text-sm" : "text-xs",
                  productHighlighted
                    ? "font-semibold text-amber-900/70 dark:text-amber-200/70"
                    : "text-muted-foreground",
                )}
              >
                ຄົງເຫຼືອ
              </span>
              <span
                className={cn(
                  "block max-w-full truncate font-semibold tabular-nums",
                  flashOnValueChange
                    ? "font-bold text-4xl leading-none"
                    : productHighlighted
                      ? "font-bold text-xl"
                      : "text-lg",
                  productHighlighted && "text-amber-900 dark:text-amber-100",
                )}
              >
                <MonitorAnimatedValue
                  value={formatProductRemaining(product)}
                  enabled={flashOnValueChange}
                  className={flashOnValueChange ? "gap-2 text-4xl" : undefined}
                />
              </span>
            </span>
          ) : null}
        </span>
        {!compactVariants && flashOnValueChange && compactDisplayLayout ? (
          <span className="flex w-full min-w-0 items-end justify-between gap-2 border-border/60 border-t pt-2">
            <span
              className={cn(
                "text-sm",
                productHighlighted
                  ? "font-semibold text-amber-900/70 dark:text-amber-200/70"
                  : "text-muted-foreground",
              )}
            >
              ຄົງເຫຼືອ
            </span>
            <span
              className={cn(
                "min-w-0 truncate font-bold text-3xl tabular-nums leading-none",
                productHighlighted && "text-amber-900 dark:text-amber-100",
              )}
            >
              <MonitorAnimatedValue
                value={formatProductRemaining(product)}
                enabled={flashOnValueChange}
                className="justify-end gap-2 text-3xl"
              />
            </span>
          </span>
        ) : null}
      </Button>

      {compactVariants ? (
        <MonitorDisplayVariants variants={product.variants} />
      ) : open ? (
        <ul
          className={cn(
            "space-y-2 border-t px-4 py-3",
            productHighlighted && HIGHLIGHT_VARIANT_PANEL_BG,
          )}
        >
          {product.variants.map((variant) => {
            const variantHighlighted =
              isVariantHighlighted?.(variant.sessionProductId) ?? false;
            const sale = variant.saleDetails;
            return (
              <li
                key={variant.sessionProductId}
                className={cn(
                  "relative flex min-w-0 items-center justify-between gap-2 rounded-md px-3 py-2 transition-all",
                  sale && "@container/monitor-variant flex-wrap items-start",
                  flashOnValueChange && !compactDisplayLayout
                    ? "px-4 py-3 text-lg"
                    : "text-sm",
                  flashOnValueChange &&
                    compactDisplayLayout &&
                    "flex-col items-stretch gap-2 px-3 py-3 text-base",
                  variantHighlighted &&
                    cn(
                      HIGHLIGHT_VARIANT_ROW_BG,
                      "monitor-variant-highlight-blink shadow-sm ring-1 ring-amber-500/50",
                    ),
                  !variantHighlighted && "bg-muted/40",
                )}
              >
                {variantHighlighted ? (
                  <span
                    className="monitor-variant-highlight-bar-blink absolute inset-y-2 left-0 w-1 rounded-r-full bg-amber-500"
                    aria-hidden
                  />
                ) : null}
                {sale ? (
                  <div className="grid w-full min-w-0 @[17rem]/monitor-variant:grid-cols-[minmax(0,1fr)_auto] gap-3">
                    <div className="min-w-0">
                      <p className="break-all font-bold font-mono text-2xl leading-tight">
                        {variant.cfCode}
                      </p>
                      {variant.variantLabel && (
                        <p className="mt-1 break-words text-muted-foreground text-sm">
                          {variant.variantLabel}
                        </p>
                      )}
                    </div>
                    <div className="min-w-0 space-y-1 @[17rem]/monitor-variant:text-right">
                      <p className="text-muted-foreground text-sm">ລາຄາຂາຍ</p>
                      <p className="break-words font-bold text-2xl tabular-nums leading-tight">
                        {formatKip(sale.salePrice)}
                      </p>
                      {sale.salePrice < sale.price && (
                        <div className="space-y-1 text-sm">
                          <s className="block text-muted-foreground tabular-nums">
                            {formatKip(sale.price)}
                          </s>
                          <p className="break-words font-medium">
                            ຫຼຸດ {formatKip(sale.price - sale.salePrice)}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <span className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2 gap-y-1 pl-0.5">
                    <span
                      className={cn(
                        "min-w-0 break-all font-medium font-mono",
                        flashOnValueChange &&
                          !compactDisplayLayout &&
                          "font-bold text-2xl tracking-wide",
                        flashOnValueChange &&
                          compactDisplayLayout &&
                          "font-bold text-xl tracking-wide",
                        variantHighlighted &&
                          !flashOnValueChange &&
                          "font-bold text-amber-950 text-base tracking-wide dark:text-amber-100",
                        variantHighlighted &&
                          flashOnValueChange &&
                          "text-amber-950 dark:text-amber-100",
                      )}
                    >
                      {variant.cfCode}
                    </span>
                    {variant.variantLabel ? (
                      <span
                        className={cn(
                          "min-w-0 break-words",
                          flashOnValueChange && !compactDisplayLayout
                            ? "text-base"
                            : undefined,
                          variantHighlighted
                            ? "font-medium text-amber-900/75 dark:text-amber-200/75"
                            : "text-muted-foreground",
                        )}
                      >
                        {variant.variantLabel}
                      </span>
                    ) : null}
                  </span>
                )}
                {(!sale || onToggleVariantHighlight) && (
                  <span className="flex min-w-0 shrink-0 items-center justify-end gap-2">
                    {!sale && (
                      <span
                        className={cn(
                          "max-w-full truncate font-semibold tabular-nums",
                          flashOnValueChange &&
                            !compactDisplayLayout &&
                            "font-bold text-3xl leading-none",
                          flashOnValueChange &&
                            compactDisplayLayout &&
                            "font-bold text-2xl leading-none",
                          variantHighlighted &&
                            !flashOnValueChange &&
                            "rounded-md bg-amber-600 px-2 py-0.5 font-bold text-sm text-white shadow-sm",
                          variantHighlighted &&
                            flashOnValueChange &&
                            !compactDisplayLayout &&
                            "rounded-md bg-amber-600 px-3 py-1 font-bold text-3xl text-white shadow-sm",
                          variantHighlighted &&
                            flashOnValueChange &&
                            compactDisplayLayout &&
                            "rounded-md bg-amber-600 px-2.5 py-1 font-bold text-2xl text-white shadow-sm",
                        )}
                      >
                        <MonitorAnimatedValue
                          value={formatRemaining(variant)}
                          enabled={flashOnValueChange}
                          className={
                            flashOnValueChange
                              ? compactDisplayLayout
                                ? "gap-2 text-2xl"
                                : "gap-2 text-3xl"
                              : undefined
                          }
                        />
                      </span>
                    )}
                    {onToggleVariantHighlight ? (
                      <Button
                        type="button"
                        variant={variantHighlighted ? "default" : "outline"}
                        size="sm"
                        className={cn(
                          "h-7 gap-1 px-2 text-xs",
                          variantHighlighted &&
                            "border-amber-600 bg-amber-600 hover:bg-amber-700",
                        )}
                        aria-pressed={variantHighlighted}
                        onClick={(event) => {
                          event.stopPropagation();
                          onToggleVariantHighlight(variant.sessionProductId);
                        }}
                      >
                        {variantHighlighted ? (
                          <RiStarFill className="size-3.5" />
                        ) : (
                          <RiStarLine className="size-3.5" />
                        )}
                        {variantHighlighted
                          ? emphasisOnLabel
                          : emphasisOffLabel}
                      </Button>
                    ) : null}
                  </span>
                )}
                {sale && (
                  <dl className="mt-1 grid w-full min-w-0 grid-cols-2 gap-3 border-border/70 border-t pt-3 text-sm">
                    <div className="min-w-0 space-y-1">
                      <dt className="text-muted-foreground">ສະຖານະຂາຍ</dt>
                      <dd>
                        <Badge
                          variant="outline"
                          className="h-auto whitespace-normal text-sm"
                        >
                          {sale.saleEnabled ? "ເປີດຂາຍ" : "ປິດຂາຍ"}
                        </Badge>
                      </dd>
                    </div>
                    <div className="min-w-0 space-y-1 text-right">
                      <dt className="text-muted-foreground">
                        {sale.isPreorder ? "ການສັ່ງຊື້" : "ຄົງເຫຼືອ"}
                      </dt>
                      <dd className="break-words font-semibold text-lg tabular-nums">
                        {sale.isPreorder ? (
                          "Preorder"
                        ) : (
                          <MonitorAnimatedValue
                            value={formatRemaining(variant)}
                            enabled={flashOnValueChange}
                            className="justify-end"
                          />
                        )}
                      </dd>
                    </div>
                    <div className="col-span-2 flex min-w-0 flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                      <dt className="text-muted-foreground">ບໍລິການເສີມ</dt>
                      <dd className="break-words font-medium">
                        {sale.optionsInherited ? "ຄ່າເລີ່ມຕົ້ນ" : "ຕັ້ງເອງ"}
                      </dd>
                    </div>
                  </dl>
                )}
              </li>
            );
          })}
        </ul>
      ) : null}
    </article>
  );
}

export function FeedProductMonitorTable({
  products,
  totals,
  isLoading,
  hasNextPage = false,
  isFetchingNextPage = false,
  onLoadMore,
  isProductOnDisplay,
  getProductDisplayPosition,
  onToggleProductDisplay,
  displayCount = 0,
  maxDisplayProducts = 3,
  displayToggleLabels,
  isProductHighlighted,
  isVariantHighlighted,
  onToggleProductHighlight,
  onToggleVariantHighlight,
  emphasisToggleLabels,
  defaultExpandProducts = true,
  emptyMessage = "ຍັງບໍ່ມີສິນຄ້າໃນເຊສຊັນ",
  flashOnValueChange = false,
  compactDisplayLayout = false,
  queueSides,
  compactVariants = false,
}: FeedProductMonitorTableProps) {
  const showDisplayHint = Boolean(onToggleProductDisplay);
  const showQueueSides = Boolean(queueSides) && products.length === 1;
  const fitVariants = compactVariants && products.length === 1;
  const { scrollRootRef, sentinelRef } = useBoundedInfiniteScroll({
    hasNextPage,
    isFetchingNextPage,
    onLoadMore,
  });
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(
    () => new Set(),
  );

  useEffect(() => {
    if (!defaultExpandProducts) return;
    setExpandedProducts((current) => {
      const next = new Set(current);
      let changed = false;
      for (const product of products) {
        if (!next.has(product.productId)) {
          next.add(product.productId);
          changed = true;
        }
      }
      return changed ? next : current;
    });
  }, [defaultExpandProducts, products]);

  return (
    <div
      className={
        fitVariants ? "flex min-h-0 flex-1 flex-col gap-3" : "space-y-5"
      }
    >
      {totals && fitVariants && (
        <div className="grid shrink-0 grid-cols-[minmax(0,1fr)_minmax(0,2fr)] gap-4 rounded-lg border border-primary/25 bg-primary/5 p-4">
          <div className="min-w-0 space-y-2">
            <p className="font-medium text-foreground/80 text-sm">
              ລູກຄ້າ CF ແລ້ວ
            </p>
            <MonitorAnimatedValue
              value={String(totals.claimQuantity)}
              enabled={flashOnValueChange}
              unit="ຊິ້ນ"
              className="flex-wrap font-bold text-4xl leading-none tracking-tight sm:text-5xl"
              unitClassName="font-medium text-lg text-muted-foreground tracking-normal sm:text-xl"
            />
          </div>
          <div className="min-w-0 space-y-2 border-primary/15 border-l pl-4 text-right">
            <p className="font-medium text-foreground/80 text-sm">
              ມູນຄ່າສິນຄ້າ CF
            </p>
            <MonitorAnimatedValue
              value={formatGroupedInteger(totals.claimValue)}
              enabled={flashOnValueChange}
              unit="ກີບ"
              className="max-w-full flex-wrap justify-end break-words font-bold text-4xl leading-none tracking-tight sm:text-5xl"
              unitClassName="font-medium text-lg text-muted-foreground tracking-normal sm:text-xl"
            />
          </div>
        </div>
      )}
      {totals && !fitVariants ? (
        <div
          className={cn(
            "grid min-w-0 gap-3",
            flashOnValueChange && compactDisplayLayout
              ? "grid-cols-1 gap-3"
              : "sm:grid-cols-2",
            flashOnValueChange && !compactDisplayLayout && "gap-4",
          )}
        >
          <div
            className={cn(
              "min-w-0 rounded-md border bg-muted/40 px-5 py-4 text-center",
              flashOnValueChange && !compactDisplayLayout && "px-6 py-5",
              flashOnValueChange && compactDisplayLayout && "px-4 py-4",
            )}
          >
            <div
              className={cn(
                "text-muted-foreground",
                flashOnValueChange ? "font-medium text-base" : "text-sm",
              )}
            >
              ຈຳນວນທີ່ລູກຄ້າ CF ແລ້ວ
            </div>
            <div
              className={cn(
                "mt-1 flex justify-center font-semibold tracking-tight",
                flashOnValueChange
                  ? compactDisplayLayout
                    ? "text-4xl leading-none"
                    : "text-5xl leading-none"
                  : "text-3xl",
              )}
            >
              <MonitorAnimatedValue
                value={String(totals.claimQuantity)}
                enabled={flashOnValueChange}
                unit="ຊິ້ນ"
                unitClassName={cn(
                  "font-medium text-muted-foreground",
                  flashOnValueChange
                    ? compactDisplayLayout
                      ? "text-xl"
                      : "text-2xl"
                    : "text-lg",
                )}
                className={cn(
                  "max-w-full justify-center overflow-hidden",
                  flashOnValueChange ? "gap-2" : undefined,
                )}
              />
            </div>
          </div>
          <div
            className={cn(
              "min-w-0 rounded-md border bg-muted/40 px-5 py-4 text-center",
              flashOnValueChange && !compactDisplayLayout && "px-6 py-5",
              flashOnValueChange && compactDisplayLayout && "px-4 py-4",
            )}
          >
            <div
              className={cn(
                "text-muted-foreground",
                flashOnValueChange ? "font-medium text-base" : "text-sm",
              )}
            >
              ມູນຄ່າສິນຄ້າທີ່ລູກຄ້າ CF
            </div>
            <div
              className={cn(
                "mt-1 flex justify-center font-semibold tracking-tight",
                flashOnValueChange
                  ? compactDisplayLayout
                    ? "break-all text-3xl leading-none sm:text-4xl"
                    : "text-5xl leading-none"
                  : "text-3xl",
              )}
            >
              <MonitorAnimatedValue
                value={formatKip(totals.claimValue)}
                enabled={flashOnValueChange}
                className={cn(
                  "max-w-full justify-center overflow-hidden",
                  flashOnValueChange ? "gap-2" : undefined,
                )}
              />
            </div>
          </div>
        </div>
      ) : null}

      {showDisplayHint ? (
        <p className="text-muted-foreground text-sm">
          ໜ້າຈໍສະແດງໄດ້ສູງສຸດ {maxDisplayProducts} ສິນຄ້າ ({displayCount}/
          {maxDisplayProducts}) — variant ທັງໝົດຂອງ product ຈະຂຶ້ນໜ້າຈໍ
        </p>
      ) : null}

      <div
        ref={scrollRootRef}
        className={
          fitVariants
            ? "min-h-0 flex-1"
            : "max-h-[42rem] overflow-auto overscroll-contain rounded-md"
        }
      >
        {isLoading && products.length === 0 ? (
          <div className="flex h-40 items-center justify-center rounded-md border text-muted-foreground text-sm">
            ກຳລັງໂຫຼດຕິດຕາມສິນຄ້າ...
          </div>
        ) : products.length === 0 ? (
          <div className="flex h-40 items-center justify-center rounded-md border text-muted-foreground text-sm">
            {emptyMessage}
          </div>
        ) : (
          <section
            aria-label={showQueueSides ? "ຄິວກ່ອນໜ້າ ສິນຄ້າປັດຈຸບັນ ແລະຄິວຖັດໄປ" : "ສິນຄ້າ"}
            className={cn(
              "grid min-w-0 gap-4",
              showQueueSides
                ? fitVariants
                  ? "grid-cols-[minmax(0,1fr)_minmax(0,4fr)_minmax(0,1fr)] items-start gap-2"
                  : "grid-cols-[minmax(0,1fr)_minmax(0,1.7fr)_minmax(0,1fr)] items-start @[50rem]/monitor:gap-4 gap-2"
                : flashOnValueChange
                  ? compactDisplayLayout
                    ? "grid-cols-1"
                    : products.length === 1
                      ? "grid-cols-1"
                      : products.length === 2
                        ? "@[40rem]/monitor:grid-cols-2 grid-cols-1"
                        : "@[40rem]/monitor:grid-cols-2 @[60rem]/monitor:grid-cols-3 grid-cols-1"
                  : "grid-cols-1 sm:grid-cols-2 xl:grid-cols-3",
            )}
          >
            {showQueueSides && queueSides?.previous}
            {products.map((product) => (
              <MonitorProductCard
                key={product.productId}
                product={product}
                open={expandedProducts.has(product.productId)}
                productOnDisplay={
                  isProductOnDisplay?.(product.productId) ?? false
                }
                displayPosition={getProductDisplayPosition?.(product.productId)}
                productHighlighted={
                  isProductHighlighted?.(product.productId) ?? false
                }
                isVariantHighlighted={isVariantHighlighted}
                displayToggleLabels={displayToggleLabels}
                emphasisToggleLabels={emphasisToggleLabels}
                flashOnValueChange={flashOnValueChange}
                compactVariants={fitVariants}
                compactDisplayLayout={compactDisplayLayout || showQueueSides}
                onToggleProductDisplay={
                  onToggleProductDisplay
                    ? () => onToggleProductDisplay(product.productId)
                    : undefined
                }
                onToggleProductHighlight={
                  onToggleProductHighlight
                    ? () => onToggleProductHighlight(product.productId)
                    : undefined
                }
                onToggleVariantHighlight={
                  onToggleVariantHighlight
                    ? (sessionProductId) =>
                        onToggleVariantHighlight(sessionProductId)
                    : undefined
                }
                onToggle={() =>
                  setExpandedProducts((current) => {
                    const next = new Set(current);
                    if (next.has(product.productId)) {
                      next.delete(product.productId);
                    } else {
                      next.add(product.productId);
                    }
                    return next;
                  })
                }
              />
            ))}
            {showQueueSides && queueSides?.next}
          </section>
        )}

        {hasNextPage ? (
          <div ref={sentinelRef} className="h-4" aria-hidden />
        ) : null}
        {isFetchingNextPage ? (
          <p className="py-2 text-center text-muted-foreground text-sm">
            ກຳລັງໂຫຼດເພີ່ມ...
          </p>
        ) : null}
      </div>
    </div>
  );
}
